/* global Sys, Core, EventHandler */
Sys.ns("Core");

/**
 * The base Controller class.
 *
 * This class should be used as base when creating a new controller.
 *
 * The purpose of the controller is to be the backbone of the module by both performing the majority of the logic as
 * well as running the communication with the outside world.
 *
 * @class Core.Controller
 * @extends Sys.Observable
 * @aside guide events__events
 */
Core.Controller = {

    /**
     * @cfg {String} name The name of the Module this controller belongs to
     */

    /**
     * @cfg model The model
     */

    /**
     * @cfg view The view
     */

    /**
     * @cfg eventHandler The event handler
     */

    /**
     * @constructs
     * @param {Object} config The configuration object.
     * @returns {void}
     */
    constructor: function(config) {
        // Local event handler used for events communicated within the module
        this.localEventHandler = config.eventHandler;

        // Global event handler used for events communicated outside the module
        this.eventHandler = EventHandler;

        this.handlers = {};
        this.init(config);
    },

    /**
     * Initializes the controller by storing references to the model and view as well as setting up the events.
     *
     * @protected
     * @param {Object} config The configuration object.
     * @returns {void}
     */
    init: function(config) {
        this.model = config.model;
        this.view = config.view;
        this.MODULE_NAME = config.name;

        this.setupEvents();
    },

    /**
     * This method sets up all the event listeners for the controller and should be overridden in all subclasses.
     *
     * If the controller wants to be able to be controlled by other modules, it should listen to one or more
     * request events. The naming convention for a request event is `request:myModuleName.action`.
     *
     * If the controller should react on events from other modules, it should listen to one or more notify events.
     * For example, the event name for when the game is entering idle state is `notify:stateHandler.enteringIdleState`.
     *
     * If the view should be able to communicate with the controller, the controller should listen to one or more
     * view events. It should be named `view:event`.
     *
     * @example
     *     var me = this;
     *     me.on({
     *         "request:myModuleName.animate"          : me.onRequestAnimate,
     *
     *         "notify:stateHandler.enteringIdleState" : me.onEnteringIdleState,
     *
     *         "view:runningAnimation"                 : me.onRunningAnimation,
     *         "view:animationComplete"                : me.onAnimationComplete
     *     });
     *
     * In this example the controller can be controlled by another module if it fires the
     * `request:myModuleName.animate` event.
     *
     * The controller gets notified when the game enters the idle state.
     *
     * The view can communicate with the controller through the `view:runningAnimation` and
     * `view:animationComplete` events.
     *
     * @protected
     * @returns {void}
     */
    setupEvents: function() {
        // Intentionally left empty
    },

    /**
     * @protected
     * @returns {void}
     */
    onModulesFinishedLoading: function() {
        this.model.setState("loaded");
    },

    /**
     * Perform action when all wait events have been received.
     *
     * @protected
     * @param {string} event The event name.
     * @param {Function} func The function to use when the event is dispatched.
     * @returns {void}
     */
    addListener: function(event, func) {
        this.handlers[event] = func;

        // If the event name starts with "view:" use the local event handler
        // since it is a module event
        if (event.indexOf("view:") === 0) {
            this.localEventHandler.addListener(this, event);
        }
        else {
            this.eventHandler.addListener(this, event);
        }
    },

    /**
     * Remove the listener for the provided event.
     *
     * @protected
     * @param {string} event The event to stop listening to.
     * @returns {void}
     */
    removeListener: function(event) {
        // If the event name starts with "view:" use the local event handler
        // since it is a module event
        if (event.indexOf("view:") === 0) {
            this.localEventHandler.removeListener(this, event);
        }
        else {
            this.eventHandler.removeListener(this, event);
        }

        this.handlers[event] = undefined;
    }
};

Core.Controller = Sys.extend(Sys.Observable, Core.Controller, "Core.Controller");
